import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DataTable from "react-data-table-component";
import { gql, useQuery, useMutation } from "@apollo/client";
import StarekContext from "../../context/StarekContext";
import EntityLoadingState from "../../components/EntityLoadingState";
import EntityEmptyState from "../../components/EntityEmptyState";
import { EMPTY_MESSAGES } from "../../constants/emptyStateMessages";
import RoleSidebar from "./RoleSidebar";
import EntityOperationBar from "../../components/EntityOperationBar";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { usePermissions } from "../../hooks/usePermissions";
import { parseRouteEntityId } from "../../utils";
import { softDeleteData, restoreData } from "../../utils/softDelete";
import "../../starek.css";

// UI Strings
const UI_STRINGS = {
    PAGE_TITLE: "Roles",
    SEARCH_PLACEHOLDER: "Search",
    // Delete confirmation
    DELETE_TITLE: "Delete Role(s)?",
    DELETE_CONFIRM: "Delete",
    DELETE_CANCEL: "Cancel",
    // Success messages
    DELETE_SUCCESS: "Role(s) deleted successfully",
    // Error messages
    DELETE_ERROR: "Failed to delete role(s)",
    NOT_FOUND: "Role not found",
};

// Confirmation messages
const ROLE_MESSAGES = {
    DELETE_ROLE_SINGLE: (name) => `You are about to delete role "${name}".

This action cannot be undone.

Are you sure you wish to continue?`,

    DELETE_ROLES_BULK: (count) => `You are about to delete ${count} role(s).

This action cannot be undone.

Are you sure you wish to continue?`,
};

const GET_ROLES = gql`
    query Roles($where: RoleWhereInput) {
        roles(where: $where) {
            id
            name
            description
            is_active
            deleted_at
            memberships {
                role_id
            }
            privileges {
                id
                entity_type_id
                permission
            }
        }
    }
`;

const UPDATE_MANY_ROLE = gql`
    mutation UpdateManyRole($data: RoleUpdateManyMutationInput!, $where: RoleWhereInput) {
        updateManyRole(data: $data, where: $where) {
            count
        }
    }
`;

const DELETE_ONE_ROLE = gql`
    mutation DeleteOneRole($id: Int!) {
        deleteOneRole(where: { id: $id }) {
            id
        }
    }
`;

const DELETE_MANY_ROLE = gql`
    mutation DeleteManyRole($ids: [Int!]!) {
        deleteManyRole(where: { id: { in: $ids } }) {
            count
        }
    }
`;

const Roles = () => {
    const starekContext = useContext(StarekContext);
    const navigate = useNavigate();
    const { id: routeId } = useParams();
    const routeRoleId = parseRouteEntityId(routeId);
    const { can } = usePermissions();
    const canWrite = can("role", "WRITE");
    const canDestroy = can("role", "DESTROY");

    const [searchParams, setSearchParams] = useSearchParams();

    // View state lives in the URL so a refresh, a deep link or a back-navigation
    // lands on exactly the same list the user was looking at.
    const showInactive = searchParams.get("showInactive") === "1";

    const setShowInactive = (value, options = {}) => {
        const next = new URLSearchParams(searchParams);
        if (value) {
            next.set("showInactive", "1");
        } else {
            next.delete("showInactive");
        }
        setSearchParams(next, options);
    };

    const { data, loading, error, refetch } = useQuery(GET_ROLES, {
        client: starekContext.engineClient,
        fetchPolicy: "network-only",
        variables: { where: showInactive ? {} : { is_active: { equals: true } } },
    });

    const [items, setItems] = useState([]);
    const [filterText, setFilterText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);
    const [showWizard, setShowWizard] = useState(false);
    const [routeEditRole, setRouteEditRole] = useState(null);
    const [confirmCfg, setConfirmCfg] = useState({ open: false });
    const inactiveRetryRef = useRef(null);
    const rolesFromQuery = useMemo(() => data?.roles ?? [], [data?.roles]);

    const [deleteOneRole, { loading: deletingOne }] = useMutation(
        DELETE_ONE_ROLE,
        {
            client: starekContext.engineClient,
        },
    );
    const [deleteManyRole, { loading: deletingMany }] = useMutation(
        DELETE_MANY_ROLE,
        {
            client: starekContext.engineClient,
        },
    );
    const [updateManyRole] = useMutation(UPDATE_MANY_ROLE, { client: starekContext.engineClient });

    const isDeleting = deletingOne || deletingMany;

    useEffect(() => {
        if (error) starekContext.showError(error);
    }, [error, starekContext]);

    useEffect(() => {
        if (!loading && data?.roles) {
            setItems(data.roles || []);
        }
    }, [data, loading]);

    useEffect(() => {
        if (!routeId) {
            setRouteEditRole(null);
            return;
        }

        if (loading) return;

        if (routeRoleId == null) {
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditRole(null);
            navigate("/roles", { replace: true });
            return;
        }

        const roleToEdit = rolesFromQuery.find(
            (role) => Number(role?.id) === routeRoleId,
        );

        if (!roleToEdit) {
            if (!showInactive && inactiveRetryRef.current !== routeRoleId) {
                inactiveRetryRef.current = routeRoleId;
                const widened = new URLSearchParams(searchParams);
                widened.set("showInactive", "1");
                setSearchParams(widened, { replace: true });
                return;
            }
            starekContext.showError(UI_STRINGS.NOT_FOUND);
            setRouteEditRole(null);
            navigate("/roles", { replace: true });
            return;
        }

        setRouteEditRole(roleToEdit);
        setSelectedRows((current) =>
            current.length === 1 && Number(current[0]?.id) === routeRoleId
                ? current
                : [roleToEdit],
        );
        setShowWizard(true);
    }, [
        routeId,
        routeRoleId,
        loading,
        rolesFromQuery,
        showInactive,
        searchParams,
        setSearchParams,
        navigate,
        starekContext,
    ]);

    const filteredItems = useMemo(() => {
        const q = filterText.trim().toLowerCase();
        if (!q) return items;
        return items.filter((r) => {
            return (
                (r.name || "").toLowerCase().includes(q) ||
                (r.description || "").toLowerCase().includes(q)
            );
        });
    }, [items, filterText]);

    const handleAdd = () => {
        setRouteEditRole(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        setShowWizard(true);
    };

    const handleEdit = () => {
        if (selectedRows.length === 1) {
            navigate(`/roles/${selectedRows[0].id}`);
        }
    };

    const handleDelete = () => {
        if (selectedRows.length === 0) return;

        const count = selectedRows.length;
        const roleName = selectedRows[0]?.name || "Unknown";

        setConfirmCfg({
            open: true,
            type: "danger",
            title: UI_STRINGS.DELETE_TITLE,
            content:
                count === 1
                    ? ROLE_MESSAGES.DELETE_ROLE_SINGLE(roleName)
                    : ROLE_MESSAGES.DELETE_ROLES_BULK(count),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            onConfirm: async () => {
                const ids = selectedRows.map((r) => Number(r.id));

                try {
                    if (ids.length === 1) {
                        await deleteOneRole({ variables: { id: ids[0] } });
                    } else {
                        await deleteManyRole({ variables: { ids } });
                    }
                    starekContext?.showSuccess?.(UI_STRINGS.DELETE_SUCCESS);
                } catch (error) {
                    starekContext.showError(
                        error?.message || UI_STRINGS.DELETE_ERROR,
                    );
                } finally {
                    setSelectedRows([]);
                    setToggleCleared((p) => !p);
                    await refetch();
                    setConfirmCfg({ open: false });
                }
            },
            onClose: () => setConfirmCfg({ open: false }),
        });
    };

    const handleDrawerClose = async () => {
        setShowWizard(false);
        setRouteEditRole(null);
        setSelectedRows([]);
        setToggleCleared((p) => !p);
        if (routeId) {
            navigate("/roles", { replace: true });
        }
        await refetch();
    };

    const handleSaveRole = async () => {
        await handleDrawerClose();
    };

    const handleDeleteRole = async (role) => {
        if (!role?.id) return;

        const roleName = role.name || "Unknown";
        setConfirmCfg({
            open: true,
            type: "danger",
            title: UI_STRINGS.DELETE_TITLE,
            content: ROLE_MESSAGES.DELETE_ROLE_SINGLE(roleName),
            confirmText: UI_STRINGS.DELETE_CONFIRM,
            cancelText: UI_STRINGS.DELETE_CANCEL,
            onConfirm: async () => {
                try {
                    await deleteOneRole({ variables: { id: Number(role.id) } });
                    starekContext?.showSuccess?.(UI_STRINGS.DELETE_SUCCESS);
                } catch (error) {
                    starekContext.showError(
                        error?.message || UI_STRINGS.DELETE_ERROR,
                    );
                } finally {
                    await handleDrawerClose();
                    setConfirmCfg({ open: false });
                }
            },
            onClose: () => setConfirmCfg({ open: false }),
        });
    };

    const activeRole = routeEditRole || selectedRows[0] || null;

    const columns = [
        {
            name: "Name",
            selector: (r) => r.name || "-",
            sortable: true,
            grow: 1.2,
            cell: (row) => (
                <span
                    role="button"
                    className="text-primary"
                    onClick={() => navigate(`/roles/${row.id}`)}
                >
                    {row.name || "-"}
                </span>
            ),
        },
        {
            name: "Description",
            selector: (r) => r.description || "-",
            sortable: false,
            grow: 2,
            cell: (row) => (
                <div className="text-truncate" style={{ maxWidth: 520 }}>
                    {row.description || "-"}
                </div>
            ),
        },
    ];

    const customStyles = {
        headCells: { style: { fontWeight: "bold" } },
        rows: { style: { minHeight: "60px" } },
        cells: {
            style: {
                paddingTop: "12px",
                paddingBottom: "12px",
                whiteSpace: "normal",
                lineHeight: "1.2rem",
            },
        },
    };

    const handleToggleActive = async (roles, makeActive) => {
        const userId = starekContext?.currentUser?.id ?? starekContext?.user?.id;
        const ids = roles.map((r) => r.id);
        try {
            await updateManyRole({
                variables: {
                    data: makeActive ? restoreData() : softDeleteData(),
                    where: { id: { in: ids } },
                },
            });
            starekContext.showSuccess(makeActive ? `Restored ${ids.length} role(s)` : `Deactivated ${ids.length} role(s)`);
            setSelectedRows([]);
            setToggleCleared((p) => !p);
            await refetch();
        } catch (err) {
            starekContext.showError(err);
        }
    };

    const roleExtendedActions = selectedRows.length > 0 ? [
        {
            label: "Deactivate",
            icon: "fa-ban",
            danger: true,
            disabled: selectedRows.every((r) => !r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => r.is_active), false),
        },
        {
            label: "Restore",
            icon: "fa-undo",
            disabled: selectedRows.every((r) => r.is_active),
            onClick: (rows) => handleToggleActive(rows.filter((r) => !r.is_active), true),
        },
    ] : [];

    const roleConditionalRowStyles = [
        { when: (r) => !r.is_active, style: { opacity: 0.5, backgroundColor: "#f8f9fa" } },
    ];

    if (loading) return <EntityLoadingState icon="fa-lock" />;

    return (
        <div className="container-fluid">
            <div className="font-weight-bold mb-3">{UI_STRINGS.PAGE_TITLE}</div>

            <EntityOperationBar
                entity={{ singular: "Role", plural: "Roles" }}
                allowSearch={true}
                allowAdd={canWrite}
                allowDelete={canDestroy}
                allowEdit={canWrite}
                allowExtendedActions={canWrite && selectedRows.length > 0}
                extendedActions={roleExtendedActions}
                allowShowInactive={true}
                showInactive={showInactive}
                onShowInactiveChange={(v) => { setShowInactive(v); setSelectedRows([]); setToggleCleared((p) => !p); }}
                selectedRows={selectedRows}
                isDeleting={isDeleting}
                searchPlaceholder={UI_STRINGS.SEARCH_PLACEHOLDER}
                onSearch={(text) => setFilterText(text)}
                onAdd={handleAdd}
                onViewChange={() => { }}
                onDelete={handleDelete}
                onEdit={handleEdit}
            >
                <EntityOperationBar.View
                    mode="list"
                    icon="fa-list"
                    tooltip="List View"
                >
                    <DataTable
                        columns={columns}
                        data={filteredItems}
                        selectableRows
                        onSelectedRowsChange={({ selectedRows }) =>
                            setSelectedRows(selectedRows)
                        }
                        clearSelectedRows={toggleCleared}
                        highlightOnHover
                        pointerOnHover
                        onRowClicked={(row) => navigate(`/roles/${row.id}`)}
                        responsive
                        pagination
                        dense
                        selectableRowsHighlight
                        selectableRowsNoSelectAll={false}
                        customStyles={customStyles}
                        noDataComponent={<EntityEmptyState icon="fa-lock" entityName="Role" messages={EMPTY_MESSAGES.roles} isFiltered={!!filterText.trim()} onAdd={handleAdd} />}
                        conditionalRowStyles={roleConditionalRowStyles}
                    />
                </EntityOperationBar.View>
            </EntityOperationBar>

            <RoleSidebar
                show={showWizard}
                item={activeRole}
                onDiscard={handleDrawerClose}
                onSave={canWrite ? handleSaveRole : null}
                onDelete={canDestroy ? handleDeleteRole : null}
                onToggleActive={canWrite ? (entity) => handleToggleActive([entity], !entity.is_active) : undefined}
            />

            <ConfirmModal
                open={confirmCfg.open}
                type={confirmCfg.type || "danger"}
                title={confirmCfg.title}
                content={confirmCfg.content}
                confirmText={confirmCfg.confirmText}
                cancelText={confirmCfg.cancelText}
                loading={isDeleting}
                onConfirm={confirmCfg.onConfirm}
                onClose={confirmCfg.onClose}
            />
        </div>
    );
};

export { Roles };
